import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  createElement
} from 'react';
import { createPortal } from 'react-dom';

export type MarqueeItem = {
  id: string;
  type: string;
};

type MarqueeRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type UseMarqueeSelectionOptions = {
  containerRef: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
  itemSelector?: string;
  onSelect: (items: MarqueeItem[]) => void;
};

const MOVE_THRESHOLD = 4;

const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'textarea',
  'label',
  '.ant-checkbox-wrapper',
  '.ant-dropdown-trigger',
  '.ant-btn',
  '[data-no-marquee]'
].join(',');

const ITEM_SELECTOR = [
  '[data-marquee-id]',
  '.draggable-item',
  '[draggable="true"]'
].join(',');

function rectsIntersect(
  a: { left: number; top: number; right: number; bottom: number },
  b: { left: number; top: number; right: number; bottom: number }
) {
  return !(
    a.right < b.left ||
    a.left > b.right ||
    a.bottom < b.top ||
    a.top > b.bottom
  );
}

export default function useMarqueeSelection({
  containerRef,
  enabled = true,
  itemSelector = '[data-marquee-id]',
  onSelect
}: UseMarqueeSelectionOptions) {
  const [selectionRect, setSelectionRect] = useState<MarqueeRect | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const stateRef = useRef({
    active: false,
    moved: false,
    startClientX: 0,
    startClientY: 0
  });

  const suppressNextClick = useCallback(() => {
    const suppress = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      document.removeEventListener('click', suppress, true);
    };
    document.addEventListener('click', suppress, true);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const getItemsInRect = (
      left: number,
      top: number,
      right: number,
      bottom: number
    ) => {
      const selection = { left, top, right, bottom };
      const nodes = container.querySelectorAll<HTMLElement>(itemSelector);
      const items: MarqueeItem[] = [];
      const seen = new Set<string>();

      nodes.forEach((node) => {
        const rect = node.getBoundingClientRect();
        if (
          rectsIntersect(selection, {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom
          })
        ) {
          const id = node.dataset.marqueeId;
          const type = node.dataset.marqueeType || 'item';
          if (!id || seen.has(`${type}:${id}`)) return;
          seen.add(`${type}:${id}`);
          items.push({ id, type });
        }
      });

      return items;
    };

    const updateBox = (clientX: number, clientY: number) => {
      const { startClientX, startClientY } = stateRef.current;
      const left = Math.min(startClientX, clientX);
      const top = Math.min(startClientY, clientY);
      const width = Math.abs(clientX - startClientX);
      const height = Math.abs(clientY - startClientY);

      setSelectionRect({ left, top, width, height });
      onSelectRef.current(
        getItemsInRect(left, top, left + width, top + height)
      );
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest(INTERACTIVE_SELECTOR)) return;
      if (target.closest(ITEM_SELECTOR)) return;

      stateRef.current = {
        active: true,
        moved: false,
        startClientX: e.clientX,
        startClientY: e.clientY
      };
    };

    const onMouseMove = (e: MouseEvent) => {
      const state = stateRef.current;
      if (!state.active) return;

      const dx = Math.abs(e.clientX - state.startClientX);
      const dy = Math.abs(e.clientY - state.startClientY);
      if (!state.moved && dx < MOVE_THRESHOLD && dy < MOVE_THRESHOLD) return;

      if (!state.moved) {
        state.moved = true;
        document.body.classList.add('marquee-selecting');
      }

      e.preventDefault();
      updateBox(e.clientX, e.clientY);
    };

    const onMouseUp = () => {
      const state = stateRef.current;
      if (!state.active) return;

      if (state.moved) {
        suppressNextClick();
      }

      stateRef.current.active = false;
      stateRef.current.moved = false;
      document.body.classList.remove('marquee-selecting');
      setSelectionRect(null);
    };

    const onDragStart = (e: DragEvent) => {
      if (stateRef.current.moved) {
        e.preventDefault();
      }
    };

    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    container.addEventListener('dragstart', onDragStart, true);

    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('dragstart', onDragStart, true);
      document.body.classList.remove('marquee-selecting');
    };
  }, [containerRef, enabled, itemSelector, suppressNextClick]);

  const marqueeBox = selectionRect
    ? createPortal(
        createElement('div', {
          className: 'marquee-selection-box',
          style: {
            position: 'fixed',
            left: selectionRect.left,
            top: selectionRect.top,
            width: selectionRect.width,
            height: selectionRect.height
          }
        }),
        document.body
      )
    : null;

  return { marqueeBox };
}
